'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';
import { ReferenceCard } from './reference-card';
import { ReferenceCardSkeleton } from './reference-card-skeleton';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import type { ReferenceCategory } from '@eliadmin/core/src/api/references/references-types';
const ALL_TAB_VALUE = '__all__';

export function ReferencesList({
  categories,
  locale,
  excludeBlogId,
}: {
  categories: ReferenceCategory[];
  locale: string;
  excludeBlogId?: string | number;
}) {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState<string>(ALL_TAB_VALUE);

  const activeCategoryId = activeTab === ALL_TAB_VALUE ? undefined : activeTab;

  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, activeCategoryId ?? ALL_TAB_VALUE, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchReferences({
        locale,
        query: {
          page: pageParam,
          results: 6,
          where: activeCategoryId ? { categories: { equals: activeCategoryId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page) {
        return lastPage?.meta?.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  const isLoading = referencesQuery.isLoading;

  const references = useMemo(() => {
    const items = referencesQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
    if (excludeBlogId == null) return items;
    return items.filter((b: any) => b?.id !== excludeBlogId);
  }, [referencesQuery.data, excludeBlogId]);

  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 pb-8 pt-12 sm:gap-10 sm:pb-10 sm:pt-16 lg:gap-12 lg:pb-12 lg:pt-24',
        'pr-0 sm:pr-4',
      )}
    >
      <Tabs
        value={activeTab}
        defaultValue={ALL_TAB_VALUE}
        onValueChange={setActiveTab}
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-6 sm:gap-8 md:gap-10 lg:gap-12',
        )}
      >
        <TabsList
          className={cn(
            'hide-scroll-bar',
            'flex h-fit w-full flex-nowrap items-start justify-start gap-2 overflow-scroll rounded-none bg-transparent py-0 md:items-center md:justify-center lg:gap-2',
          )}
        >
          <TabsTrigger
            value={ALL_TAB_VALUE}
            className={cn(
              'ea-references__tabs-trigger',
              'h-10 rounded-none border px-6 font-oswald text-sm font-normal uppercase sm:h-12 sm:text-base lg:h-[52px] lg:text-lg',
            )}
          >
            {t('text1947')}
          </TabsTrigger>

          {categories.map(category => (
            <TabsTrigger
              value={String(category.id)}
              key={category.id}
              className={cn(
                'ea-references__tabs-trigger',
                'h-10 rounded-none border px-6 font-oswald text-sm font-normal uppercase sm:h-12 sm:text-base lg:h-[52px] lg:text-lg',
              )}
            >
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent
          value={activeTab}
          className="grid w-full grid-cols-1 gap-4 pr-4 sm:pr-0 md:grid-cols-2 md:gap-5 lg:gap-6"
        >
          {referencesQuery.error && (
            <Paragraph className="col-span-1 pt-8 text-center md:col-span-2">
              {t('text2364')}
            </Paragraph>
          )}

          {isLoading ? (
            [...Array(9)].map((_, index) => <ReferenceCardSkeleton key={index} />)
          ) : references.length > 0 ? (
            <>
              {references.map((reference: any) => (
                <ReferenceCard key={reference.id} reference={reference} showCategory />
              ))}
            </>
          ) : (
            <Paragraph className="col-span-1 text-center sm:text-lg md:col-span-2">
              {t('text2376')}
            </Paragraph>
          )}
        </TabsContent>

        <div
          className={cn('flex w-full items-center justify-center', {
            hidden: !referencesQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => referencesQuery.fetchNextPage()}
            isLoading={referencesQuery.isFetchingNextPage}
            className="w-full sm:w-fit"
          >
            {t('text3209')}
          </Button>
        </div>
      </Tabs>
    </section>
  );
}
